import './styles.scss';

import { useCallback, useEffect, useMemo } from 'react';
import useTripSettings, {
  ColumnKeyEnum,
  IntervalUnitEnum,
  TRIPS_SETTINGS_STORAGE_KEY,
} from '@/features/Trips/components/TripSettingsModal/hook';

import DynamicContent from '@/components/DynamicContent';
import EnumDropdown from '@/components/EnumDropdown';
import { Form } from 'react-bootstrap';
import NumberInput from '@/components/NumberInput';
import { properCase } from '@/utils';

const TripSettingsModal = (): JSX.Element => {
  const setState = useTripSettings(({ setState }) => setState);
  const saveState = useTripSettings(({ saveState }) => saveState);
  const show = useTripSettings(({ state }) => state.show);
  const remember = useTripSettings(({ state }) => state.remember);
  const columns = useTripSettings(({ state }) => state.columns);
  const tripsPagePollInterval = useTripSettings(({ state }) => state.tripsPagePollInterval);
  const tripsPagePollIntervalUnit = useTripSettings(({ state }) => state.tripsPagePollIntervalUnit);
  const onToggle = useTripSettings(({ state }) => state.onToggleColumn);

  const handleToggle = useCallback(
    (key: ColumnKeyEnum): (() => void) =>
      (): void =>
        onToggle(key),
    [onToggle]
  );

  const handleHideSettings = (): void => {
    setState((current) => ({ ...current, show: false }));
    if (remember) saveState();
    else localStorage.removeItem(TRIPS_SETTINGS_STORAGE_KEY);
  };

  const onToggleRemember = (): void => {
    setState((current) => ({ ...current, remember: !current.remember }));
  };

  const minMax: { min: number; max: number } =
    tripsPagePollIntervalUnit === IntervalUnitEnum.SECONDS ? { min: 30, max: 59 } : { min: 1, max: 10 };

  const converted: number = useMemo(
    () => (tripsPagePollInterval ? millisecondsToUnit(tripsPagePollInterval, tripsPagePollIntervalUnit) : null),
    [tripsPagePollInterval, tripsPagePollIntervalUnit]
  );

  useEffect(() => {
    const value = converted < minMax.min ? minMax.min : converted > minMax.max ? minMax.max : null;
    if (value === null) return;
    setState((current) => ({
      ...current,
      tripsPagePollInterval: unitToMilliseconds(value, current.tripsPagePollIntervalUnit),
    }));
  }, [converted, minMax]);

  return (
    <DynamicContent className="PageSettings" show={show} onHide={handleHideSettings} modal drawer as="drawer">
      <DynamicContent.Title>Settings</DynamicContent.Title>
      <div>
        <Form.Check onChange={onToggleRemember} label="Remember" checked={remember} />
      </div>
      <div className="my-2 p-2">
        <h5>Columns</h5>
        {Array.from(columns?.entries()).map(
          ([key, value]: [ColumnKeyEnum, boolean]): JSX.Element => (
            <Form.Switch name={key} label={columnKeyToDisplayName(key)} checked={value || false} onChange={handleToggle(key)} key={key} />
          )
        )}
      </div>
      <div className="my-2 p-2">
        <h5>Refresh Timer</h5>
        <div className="GroupInputWithDropdown">
          <NumberInput
            name="pageRefreshTimer"
            value={converted}
            onChange={(val) => {
              setState((current) => ({ ...current, tripsPagePollInterval: unitToMilliseconds(val, current.tripsPagePollIntervalUnit) }));
            }}
          />
          <EnumDropdown
            name="pageRefreshTimerIntervalUnit"
            value={tripsPagePollIntervalUnit}
            enum={IntervalUnitEnum}
            onChange={(val) => {
              if (!val) return;
              setState((current) => ({ ...current, tripsPagePollIntervalUnit: val }));
            }}
            options={{ showClearButton: false }}
          />
        </div>
      </div>
    </DynamicContent>
  );
};

const millisecondsToUnit = (milliseconds: number, unit: IntervalUnitEnum): number => {
  switch (unit) {
    case IntervalUnitEnum.SECONDS:
      return milliseconds / 1000;
    case IntervalUnitEnum.MINUTES:
      return milliseconds / 1000 / 60;
  }
};

const unitToMilliseconds = (value: number, unit: IntervalUnitEnum): number => {
  switch (unit) {
    case IntervalUnitEnum.SECONDS:
      return value * 1000;
    case IntervalUnitEnum.MINUTES:
      return value * 1000 * 60;
  }
};

const columnKeyToDisplayName = (key: ColumnKeyEnum): string => {
  let words: string[];
  switch (key) {
    case ColumnKeyEnum.SCHEDULED_DATE:
      return 'Date';
    case ColumnKeyEnum.SCHEDULED_TIME:
      return 'Time';
    default:
      words = key.match(/([A-Z]+(?![a-z])|[A-Z]?[a-z]+)/g);
      if (!words) return properCase(key);
      return words.map((word) => (/^[A-Z]+$/.test(word) ? word : properCase(word))).join(' ');
  }
};

export default TripSettingsModal;
